import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import {cn} from '../lib/utils';
import {cva, VariantProps} from 'class-variance-authority';

const tabsListVariants = cva(
  'inline-flex h-9 items-center justify-center rounded-lg text-muted-foreground',
  {
    variants: {
      variant: {
        default: cn(
          'bg-muted p-1',
          '[&>.tabs-trigger[data-state=active]]:shadow-sm',
        ),
        outline: cn(
          '[&>.tabs-trigger]:border [&>.tabs-trigger]:border-transparent',
          '[&>.tabs-trigger[data-state=active]]:border-border',
        ),
        filter: cn(
          'bg-transparent gap-1.5',
          '[&>.tabs-trigger]:bg-card/50 [&>.tabs-trigger]:rounded-3xl [&>.tabs-trigger]:px-2.5 [&>.tabs-trigger]:py-2 [&>.tabs-trigger]:gap-2 [&>.tabs-trigger]:text-xs',
          '[&>.tabs-trigger:hover]:bg-muted [&>.tabs-trigger:hover]:text-foreground',
          '[&>.tabs-trigger[data-state=active]]:bg-card [&>.tabs-trigger[data-state=active]]:text-foreground [&>.tabs-trigger[data-state=active]]:shadow-none',
        ),
        navigation: cn(
          'flex-col w-full h-auto',
          '[&>.tabs-trigger[data-state=active]]:bg-muted',
          '[&>.tabs-trigger[data-state=active]]:text-foreground',
          '[&>.tabs-trigger[data-state=active]]:border-transparent',
          '*:w-full *:justify-start *:py-2',
        ),
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({className, variant, ...props}, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(tabsListVariants({variant}), className)}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

type ChildrenWithProps = React.ReactElement<{
  className?: string;
  children?: React.ReactNode;
}>;

interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  Icon?: React.ComponentType<{className?: string}>;
  count?: number;
  indicator?: React.ReactNode;
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({className, Icon, count, indicator, children, asChild, ...props}, ref) => {
  const mergedClassName = cn(
    'tabs-trigger',
    'inline-flex items-center justify-center gap-x-1 whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-colors',
    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    'data-[state=active]:bg-background data-[state=active]:text-foreground hover:text-foreground',
    indicator && 'relative',
    className,
  );

  const iconMarkup = Icon && <Icon className="w-4 h-4" />;
  const countMarkup = typeof count === 'number' && count > 0 && (
    <span className="text-muted-foreground font-light">• {count}</span>
  );
  const indicatorMarkup = indicator && (
    <span className="absolute -top-0.5 -right-0.5">{indicator}</span>
  );

  let childrenMarkup: React.ReactNode;
  if (asChild) {
    const childWithProps = children as ChildrenWithProps;
    childrenMarkup = React.isValidElement(childWithProps)
      ? React.cloneElement(childWithProps, {
          className: cn(childWithProps.props.className, mergedClassName),
          children: (
            <>
              {iconMarkup}
              {childWithProps.props.children}
              {countMarkup}
              {indicatorMarkup}
            </>
          ),
        })
      : null;
  } else {
    return (
      <TabsPrimitive.Trigger ref={ref} className={mergedClassName} {...props}>
        {iconMarkup}
        {children}
        {countMarkup}
        {indicatorMarkup}
      </TabsPrimitive.Trigger>
    );
  }

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      asChild={asChild}
      className={mergedClassName}
      {...props}
    >
      {childrenMarkup}
    </TabsPrimitive.Trigger>
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({className, ...props}, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export {Tabs, TabsList, TabsTrigger, TabsContent};
